import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineCommandLine } from 'react-icons/hi2';
import ConfirmDialog from '../../../components/confirm-dialog/confirm-dialog';
import DataResourceList from '../../../components/data-resource-list';
import { useAppInfo } from '../../../helpers/hooks/common-hook';
import { codehookDelete, listCodehook } from '../../../services/model/model-config-service';

export default function Index() {
  const { appId } = useAppInfo();
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listCodehook(appId);
      if (!Array.isArray(data)) throw new Error('Unable to load functions. Please try again.');
      setDataList(data);
    } catch (error) {
      setError('Unable to load functions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleConfirmDelete = async confirmed => {
    const id = deletingId;
    setDeletingId(null);
    if (!confirmed) return;
    setLoading(true);
    setError('');
    try {
      const result = await codehookDelete(appId, id);
      if (result?.status && (result.status < 200 || result.status >= 300)) throw new Error();
      await fetchConfigs();
    } catch (_) {
      setError('Unable to delete this function. Please try again.');
      setLoading(false);
    }
  };

  const processDataList = dataList.map(item => ({
    record: item,
    searchText: `${item.codehookId} ${item.functionInfo?.runtime || ''}`,
    columnsData: [
      { data: <Link to={`/app/${appId}/functions/save/${item.codehookId}`} className="rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">{item.codehookId}</Link>, isIdField: true },
      { data: <span className="inline-flex rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 font-mono text-xs text-gray-600">{item.functionInfo?.runtime || '—'}</span> },
      { data: item.modifiedAt, isUpdatedField: true },
      { options: [
        { name: 'Edit function', link: `/app/${appId}/functions/save/${item.codehookId}` },
        { name: 'Delete function', onClick: () => setDeletingId(item.codehookId) },
      ] },
    ],
  }));
  const runtimes = [...new Set(dataList.map(item => item.functionInfo?.runtime).filter(Boolean))].sort();

  return (
    <DataResourceList key={appId}
      title="Functions" description="Extend your agent with custom code and reusable actions."
      icon={HiOutlineCommandLine} items={processDataList} headers={['Function', 'Runtime']}
      loading={loading} error={error} onRefresh={fetchConfigs}
      createLabel="Add function" createPath={`/app/${appId}/functions/create`}
      emptyTitle="Create your first function" emptyDescription="Give your agent new capabilities with custom code it can run during a conversation."
      filterLabel="All runtimes" filters={runtimes.map(runtime => ({ value: runtime, label: runtime }))}
      getFilterValue={item => item.functionInfo?.runtime}
    >
      {deletingId && <ConfirmDialog open appId={appId} onCompletion={handleConfirmDelete}
        title="Delete function" message={`Delete “${deletingId}”? This action cannot be undone.`} />}
    </DataResourceList>
  );
}
